// ═══════════════════════════════════════════════════════════════════
// AemiliaGuide.js — AEMILIA, THE EMPIRE'S GUIDE (the Navi of the IE)
// MASTER COPY: gamemaster/engine/AemiliaGuide.js — vendored byte-identical.
// Reads aemiliaQuestions.js, markData.js (fonts) and ieSocial.js (the
// suppression signal), all of which ride in the same kit.
// ───────────────────────────────────────────────────────────────────
// caP'n, 2026-10-20: "a Navi type character … in the menu backdrop of
// every game … not just a ball of light with wings … either as an
// actual backdrop and/or a separate button you can press if you don't
// want it invading your backdrop." Name: "Aemilia … the one true name".
// She is also the SYSTEM's name and the default partner a fresh install gets
// (caP'n's own partner stays V'ael). Who she is beyond that is open — caP'n is
// building her 3D model; the first sketch borrowed the Lyrixi look. Look: concept A,
// "The Chime", as a flat painted placeholder until caP'n paints her.
//
// ⭐ ONE LAYER, EVERY GAME. She does NOT live inside a title's scene —
// that is how she would end up drawn and behaving differently per game.
// She is a layer mounted in index.js beside <App/>, exactly like the
// Friends pill (IESocialDock), so she perches, drifts, answers and
// hides the same way everywhere. A title only says two things:
//   1. WHERE ITS CHROME IS — the `button` / `buttonTouch` / `perches`
//      props on the mount, so she never sits on a control.
//   2. WHAT IT CAN OPEN — useAemiliaPlaces({ "name-change": fn, … })
//      from wherever that state lives. Unregistered = no "Take me there".
//
// HOW SHE SHOWS (the player picks, inside her sheet; remembered per
// device in localStorage — zero drain, no Firebase):
//   "backdrop" — DEFAULT (2026-10-21). She lives in the MENU BACKDROP
//              (MenuBackdrop.js): nothing of her on screen until you open the
//              menu, where she sits in her own nook. No backdrop mounted on
//              this screen → she behaves as "scene".
//   "scene"  — out on the screen: she perches over the title and drifts
//              between a few safe spots. Tap her to ask.
//   "button" — one small "Ask Aemilia" pill. Nothing moving on screen.
//   "off"    — gone completely (caP'n: "a way to fully hide it with a checkbox
//              and the only way to bring it back up is with a keyboard
//              shortcut"). The shortcut is "?" — so the box only appears where
//              there is a keyboard (any fine pointer); a phone never gets a way
//              to lock itself out.
//
// SIGNALS (module-level, like ieSocial's setSocialHidden — no provider):
//   useAemiliaPlaces(map)  — a title registers what it can open
//   useAemiliaMode()       — which of the four she is (the backdrop reads it)
//   openAemilia()          — open her sheet from anywhere (her nook)
//   useAemiliaOpen()       — a title reads "her sheet is open" to pause
//                            movement and hide its own pills
//   setAemiliaHidden(bool, reason) — a title hides her (login, indoors,
//                            cutscenes…). Reasons stack: she shows only when
//                            every reason that hid her has let go.
//   She also steps aside whenever the title raises setSocialHidden — the
//   same "a modal is up" predicate the Friends pill already obeys.
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from "react";
import { MK, injectMarkChrome } from "./markData";
import { useSocialHidden } from "./ieSocial";
import { EMPIRE, GAMES, HERSELF, GREETING } from "./aemiliaQuestions";
// Her HOUSE: the menu backdrop (MenuBackdrop.js draws her nook from useAemiliaMode).
// While no backdrop is mounted, "backdrop" mode shows her in the scene instead.
import { useBackdropActive, useBand } from "./ieBackdrop";

const NAME = "Aemilia";

// Her own twilight palette (sapphire and amethyst). Same in every game — she is Empire chrome,
// like the Mark, not a guest wearing each title's theme.
const A = {
  panel: "rgba(14,11,36,0.97)",
  panelHi: "#1d1844",
  edge: "#3a3170",
  edgeHot: "#8a6cf0",
  text: "#ece8ff",
  dim: "#bdb6e0",          // 8.9:1 on panel
  star: "#ffd88a",
  ink: "#1b1440",
  bubble: "#f3eeff",
};

const IS_TOUCH = typeof window !== "undefined" &&
  ("ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0);
const LS_MODE = "ie.aemilia.mode";
const LS_SEEN = "ie.aemilia.seen";
const LS_BACK = "ie.aemilia.back";        // what she was before you hid her
const MODES = ["backdrop", "scene", "button", "off"];
const LS_V2 = "ie.aemilia.v2";          // 2026-10-21: the default moved from "scene" to "backdrop"
const BACK_KEY = "?";
const HAS_KEYBOARD = typeof window !== "undefined" && !!window.matchMedia &&
  window.matchMedia("(any-pointer: fine)").matches;

function lsGet(k) { try { return window.localStorage.getItem(k); } catch { return null; } }
function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch { /* private window: fine */ } }

// ── SIGNALS ─────────────────────────────────────────────────────────
function signal(initial) {
  let value = initial;
  const subs = new Set();
  return {
    subs,
    get: () => value,
    set: (v) => { if (v === value) return; value = v; subs.forEach((fn) => { try { fn(v); } catch { /* one bad listener never stops the rest */ } }); },
  };
}
function useSignal(sig) {
  const [v, setV] = useState(sig.get);
  useEffect(() => { setV(sig.get()); sig.subs.add(setV); return () => { sig.subs.delete(setV); }; }, [sig]);
  return v;
}
const openSig = signal(false);
const hiddenSig = signal(false);
const _hideReasons = new Set();
const placesSig = signal({});        // id -> { token, run }
const modeSig = signal("backdrop");  // published so the menu backdrop knows if she lives there
const askSig = signal(0);            // openAemilia() bumps it
const unseenSig = signal(0);         // how many questions are still NEW (her nook's "?")

export const useAemiliaOpen = () => useSignal(openSig);
export const useAemiliaMode = () => useSignal(modeSig);
export const useAemiliaUnseen = () => useSignal(unseenSig);
// Opens her sheet from anywhere (her nook in the menu backdrop).
export const openAemilia = () => askSig.set(askSig.get() + 1);

// What she shows as on first load. "scene" was the default until 2026-10-21 and
// nobody had picked it on purpose yet (she had never been seen), so a stored
// "scene" without the v2 flag moves to her new home once; any choice made
// after that is kept.
function initialMode() {
  let m = lsGet(LS_MODE);
  if (!lsGet(LS_V2)) { if (m === "scene") m = "backdrop"; lsSet(LS_V2, "1"); if (m) lsSet(LS_MODE, m); }
  return MODES.includes(m) ? m : "backdrop";
}
export function setAemiliaHidden(v, reason = "title") {
  if (v) _hideReasons.add(reason); else _hideReasons.delete(reason);
  hiddenSig.set(_hideReasons.size > 0);
}

// Register what this screen can open. ⚠ One owner per id: if two mounted
// screens register the same id, the later wins and the earlier is NOT
// restored when the later unmounts (no title does this today). Functions may change every render;
// the registry holds a ref, so only the SET of ids re-registers.
export function useAemiliaPlaces(places) {
  const ref = useRef(places);
  ref.current = places;
  const ids = Object.keys(places || {}).filter((k) => typeof (places || {})[k] === "function").sort().join("|");
  useEffect(() => {
    if (!ids) return undefined;
    const token = {};
    const list = ids.split("|");
    const next = { ...placesSig.get() };
    list.forEach((id) => { next[id] = { token, run: () => { const fn = ref.current && ref.current[id]; if (typeof fn === "function") fn(); } }; });
    placesSig.set(next);
    return () => {
      const after = { ...placesSig.get() };
      list.forEach((id) => { if (after[id] && after[id].token === token) delete after[id]; });
      placesSig.set(after);
    };
  }, [ids]);
}

// ── KEYFRAMES (once per page) ───────────────────────────────────────
function injectKeys() {
  if (typeof document === "undefined" || document.getElementById("aem-keys")) return;
  const st = document.createElement("style");
  st.id = "aem-keys";
  st.textContent = `
@keyframes aemBob { 0% { transform: translateY(0) } 100% { transform: translateY(-7px) } }
@keyframes aemWingL { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(-9deg) } }
@keyframes aemWingR { 0%,100% { transform: rotate(0deg) } 50% { transform: rotate(9deg) } }
@keyframes aemPing { 0% { transform: scale(.85); opacity: .9 } 100% { transform: scale(1.15); opacity: 1 } }
@keyframes aemIn { from { transform: translateY(12px); opacity: 0 } to { transform: none; opacity: 1 } }
.aem-wl { transform-origin: 150px 154px; animation: aemWingL 1.6s ease-in-out infinite }
.aem-wr { transform-origin: 150px 154px; animation: aemWingR 1.6s ease-in-out infinite }
.aem-q:hover, .aem-q:focus-visible { background: ${A.panelHi} !important; border-color: ${A.edgeHot} !important; outline: none }
.aem-btn:focus-visible { outline: 2px solid ${A.star}; outline-offset: 2px }
@media (prefers-reduced-motion: reduce) {
  .aem-wl, .aem-wr, .aem-bob { animation: none !important }
  .aem-perch { transition: none !important }
}`;
  document.head.appendChild(st);
}

// ── HER (placeholder art: concept A, "The Chime") ───────────────────
// ⚑ When caP'n paints her, pass `art` (a CDN URL — ie-cdn-assets) and
// this SVG stays as the fail-safe.
export function AemiliaSprite({ size = 64, art = null }) {
  if (art) return <img src={art} alt="" width={size} height={size} style={{ display: "block", objectFit: "contain" }} />;
  const h = Math.round(size * 320 / 300);
  return (
    <svg viewBox="0 0 300 320" width={size} height={h} aria-hidden="true" style={{ display: "block", overflow: "visible" }}>
      <defs>
        <radialGradient id="aemGlow" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#c9b6ff" stopOpacity="0.5" /><stop offset="100%" stopColor="#c9b6ff" stopOpacity="0" /></radialGradient>
        <linearGradient id="aemBody" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#9fb6ff" /><stop offset="100%" stopColor="#b07cff" /></linearGradient>
        <linearGradient id="aemWing" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" /><stop offset="100%" stopColor="#8fd3ff" stopOpacity="0.18" /></linearGradient>
        <radialGradient id="aemHeart" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#fff6d8" /><stop offset="100%" stopColor="#ffd88a" stopOpacity="0" /></radialGradient>
      </defs>
      <circle cx="150" cy="160" r="135" fill="url(#aemGlow)" />
      <g className="aem-wl">
        <path d="M146 150 C 100 86, 46 78, 36 112 C 58 134, 108 150, 146 158 Z" fill="url(#aemWing)" stroke="#e7dcff" strokeOpacity="0.6" />
        <path d="M146 162 C 108 180, 78 214, 90 234 C 114 222, 138 192, 148 168 Z" fill="url(#aemWing)" stroke="#e7dcff" strokeOpacity="0.5" />
      </g>
      <g className="aem-wr">
        <path d="M154 150 C 200 86, 254 78, 264 112 C 242 134, 192 150, 154 158 Z" fill="url(#aemWing)" stroke="#e7dcff" strokeOpacity="0.6" />
        <path d="M154 162 C 192 180, 222 214, 210 234 C 186 222, 162 192, 152 168 Z" fill="url(#aemWing)" stroke="#e7dcff" strokeOpacity="0.5" />
      </g>
      <path d="M150 196 C 150 232, 118 250, 132 280 C 146 306, 192 302, 198 274" fill="none" stroke="url(#aemBody)" strokeWidth="7" strokeLinecap="round" />
      <path d="M198 274 C 200 262, 212 258, 216 266" fill="none" stroke="#ffd88a" strokeWidth="3" strokeLinecap="round" />
      <path d="M139 140 C 137 166, 142 186, 150 202 C 158 186, 163 166, 161 140 Z" fill="url(#aemBody)" />
      <path d="M141 146 C 130 158, 126 170, 128 180" fill="none" stroke="#9fb6ff" strokeWidth="3" strokeLinecap="round" />
      <path d="M159 146 C 170 158, 174 170, 172 180" fill="none" stroke="#9fb6ff" strokeWidth="3" strokeLinecap="round" />
      <circle cx="150" cy="157" r="16" fill="url(#aemHeart)" />
      <polygon points="150,147 156,157 150,168 144,157" fill="#fff1c4" />
      <path d="M141 102 L136 78 M136 78 L130 62 M136 78 L142 63" fill="none" stroke="#ffd88a" strokeWidth="3" strokeLinecap="round" />
      <path d="M159 102 L164 78 M164 78 L170 62 M164 78 L158 63" fill="none" stroke="#ffd88a" strokeWidth="3" strokeLinecap="round" />
      <circle cx="150" cy="118" r="20" fill="#ddd2ff" />
      <path d="M131 112 C 136 96, 164 96, 169 112 C 160 104, 140 104, 131 112 Z" fill="#8a6cf0" />
      <ellipse cx="143" cy="121" rx="2.8" ry="3.8" fill="#1b1440" />
      <ellipse cx="157" cy="121" rx="2.8" ry="3.8" fill="#1b1440" />
      <circle cx="144" cy="119.5" r="1" fill="#ffffff" />
      <circle cx="158" cy="119.5" r="1" fill="#ffffff" />
      <path d="M146 129 Q 150 132 154 129" fill="none" stroke="#6a52c9" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

// Safe spots in viewport % — a title passes its own to clear its chrome.
const DEFAULT_PERCHES = [{ x: 5, y: 36 }, { x: 94, y: 42 }, { x: 6, y: 60 }];
const SPRITE = 64;

// ═══════════════════════════════════════════════════════════════════
export default function AemiliaGuide({
  game,                                   // key into GAMES ("pier")
  button = { left: 14, bottom: 76 },      // where her pill / sheet anchor sits
  buttonTouch = null,                     // the same on a touch device (clears a D-pad / wrapped bars)
  perches = DEFAULT_PERCHES,
  minTop = 70,                            // px her perch must stay below (the title's top HUD)
  art = null,
}) {
  const titleQs = (GAMES[game] && GAMES[game].questions) || [];
  const titleName = (GAMES[game] && GAMES[game].name) || "This game";
  const allIds = [...EMPIRE, ...titleQs, ...HERSELF].map((x) => x.id);

  const [mode, setModeState] = useState(initialMode);
  useEffect(() => { modeSig.set(mode); }, [mode]);
  // "backdrop" with no backdrop on this screen = she perches in the scene.
  const backdropActive = useBackdropActive();
  const shown = mode === "backdrop" && !backdropActive ? "scene" : mode;
  // "Just a button" on a computer with the band up: her button lives IN the band's
  // top row (MenuBackdrop draws it), so she draws nothing of her own but the sheet.
  const _bv = useBand();
  const bandUp = _bv.active && _bv.faced;   // a SHARED band face, not a title's own (Terror)
  const inBand = bandUp && (shown === "backdrop" || shown === "button");
  const [open, setOpenState] = useState(false);
  const [tab, setTab] = useState(titleQs.length ? "game" : "empire");
  const [picked, setPicked] = useState(null);
  const [seen, setSeen] = useState(() => { try { return new Set(JSON.parse(lsGet(LS_SEEN) || "[]")); } catch { return new Set(); } });
  const [perch, setPerch] = useState(0);
  const [vp, setVp] = useState(() => (typeof window === "undefined" ? { w: 1280, h: 800 } : { w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);

  const socialHidden = useSocialHidden();
  const titleHidden = useSignal(hiddenSig);
  const places = useSignal(placesSig);
  const closeRef = useRef(null);
  const sheetRef = useRef(null);
  const triggerRef = useRef(null);
  const wasOpen = useRef(false);
  const skipReturn = useRef(false);   // "Take me there" opened something: let IT keep focus

  useEffect(() => { injectMarkChrome(); injectKeys(); }, []);

  const setOpen = useCallback((v) => { setOpenState(v); openSig.set(v); if (!v) setPicked(null); }, []);
  useEffect(() => () => openSig.set(false), []);

  const setMode = (m) => {
    if (m === "off" && mode !== "off") lsSet(LS_BACK, mode);
    setModeState(m); lsSet(LS_MODE, m);
  };

  // Hidden completely: "?" brings her back, as she was, with her sheet open.
  // Never while you're typing (chat boxes, name fields).
  useEffect(() => {
    if (mode !== "off") return undefined;
    const onKey = (e) => {
      if (e.key !== BACK_KEY || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const back = lsGet(LS_BACK);
      const m = ["backdrop", "scene", "button"].includes(back) ? back : "backdrop";
      setModeState(m); lsSet(LS_MODE, m); setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, setOpen]);

  // A title that hides her (indoors) while her sheet is open closes it too,
  // or it springs back over the next screen.
  useEffect(() => { if (titleHidden && open) setOpen(false); }, [titleHidden, open, setOpen]);

  // openAemilia() — her nook in the menu backdrop asks for the sheet.
  const ask = useSignal(askSig);
  const askSeen = useRef(ask);
  useEffect(() => { if (ask !== askSeen.current) { askSeen.current = ask; if (!titleHidden) setOpen(true); } }, [ask, titleHidden, setOpen]);

  // Escape closes; focus lands on the close button so a keyboard player
  // is inside the sheet.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => closeRef.current && closeRef.current.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open, setOpen]);

  // Focus back on her (or her pill) when the sheet closes.
  useEffect(() => {
    if (wasOpen.current && !open && !skipReturn.current && triggerRef.current) triggerRef.current.focus();
    if (!open) skipReturn.current = false;
    wasOpen.current = open;
  }, [open]);

  // The drift: a new safe spot every 22–38 s while she's in the scene.
  // Reduced motion: she stays put on her first perch.
  useEffect(() => {
    if (shown !== "scene" || open || perches.length < 2) return undefined;
    const still = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) return undefined;
    const id = setTimeout(() => setPerch((p) => (p + 1 + Math.floor(Math.random() * (perches.length - 1))) % perches.length), 22000 + Math.random() * 16000);
    return () => clearTimeout(id);
  }, [shown, open, perch, perches.length]);

  const unseen = allIds.filter((id) => !seen.has(id)).length;
  useEffect(() => { unseenSig.set(unseen); }, [unseen]);
  const markSeen = (id) => {
    if (seen.has(id)) return;
    const next = new Set(seen); next.add(id); setSeen(next);
    lsSet(LS_SEEN, JSON.stringify([...next]));
  };

  // She steps aside for the title's modals — but never hides her own open
  // sheet (the title raises the social signal BECAUSE her sheet is open).
  if (titleHidden || (socialHidden && !open) || ((mode === "off" || shown === "backdrop" || (shown === "button" && bandUp)) && !open)) return null;

  const anchor = (IS_TOUCH && buttonTouch) || button;

  const list = tab === "empire" ? EMPIRE : tab === "game" ? titleQs : HERSELF;
  const cur = [...EMPIRE, ...titleQs, ...HERSELF].find((x) => x.id === picked) || null;
  const go = cur && cur.place && places[cur.place];
  const narrow = vp.w < 560;

  // ⚠ SHE IS ABOVE THE TITLE'S CHROME, NOT BEHIND IT. A title's root is
  // usually position:fixed, which makes its own stacking context, so her
  // layer (a sibling of <App/>) paints over ALL of it whatever the z-index.
  // So a perch is only used if it clears the title's top HUD (`minTop`) and
  // her own button's corner stack; with none left (a short landscape
  // phone) she shows as the pill instead of sitting on a control.
  const minBottom = anchor.bottom + 56;
  const usable = perches.filter((q) => {
    const top = q.y / 100 * vp.h - SPRITE / 2;
    return top >= minTop && top + SPRITE + 12 <= vp.h - minBottom;
  });
  const asPill = shown === "button" || usable.length === 0;
  const tabs = [
    ...(titleQs.length ? [["game", titleName]] : []),
    ["empire", "Everywhere"],
    ["her", "About me"],
  ];

  const p = usable[perch % Math.max(1, usable.length)] || DEFAULT_PERCHES[0];

  return (
    <>
      {/* ── IN THE SCENE: she perches on a safe spot (see the ⚠ above) ── */}
      {!open && !asPill && (
        <button
          ref={triggerRef}
          type="button" className="aem-btn aem-perch"
          aria-label={`Ask ${NAME}${unseen ? " (something new)" : ""}`}
          title={`Ask ${NAME}`}
          onClick={() => setOpen(true)}
          style={{
            position: "fixed", zIndex: 95,
            // clamped so a 94vw perch never hangs off a 360px phone
            left: `clamp(8px, calc(${p.x}vw - ${SPRITE / 2}px), calc(100vw - ${SPRITE + 16}px))`,
            top: `clamp(8px, calc(${p.y}vh - ${SPRITE / 2}px), calc(100vh - ${SPRITE + 28}px))`,
            transition: "left 4.5s ease-in-out, top 4.5s ease-in-out",
            width: SPRITE + 8, height: SPRITE + 12, padding: 0,
            background: "none", border: "none", cursor: "pointer",
          }}>
          <div className="aem-bob" style={{ animation: "aemBob 2.2s ease-in-out infinite alternate" }}>
            <AemiliaSprite size={SPRITE} art={art} />
          </div>
          {unseen > 0 && (
            <span aria-hidden="true" style={{
              position: "absolute", top: -2, right: -2, minWidth: 18, height: 18, borderRadius: 9,
              background: A.star, color: A.ink, fontFamily: MK.sans, fontWeight: 700, fontSize: 12,
              lineHeight: "18px", textAlign: "center", animation: "aemPing 1.1s ease-in-out infinite alternate",
            }}>?</span>
          )}
        </button>
      )}

      {/* ── JUST A BUTTON ── */}
      {!open && asPill && (
        <button
          ref={triggerRef}
          type="button" className="aem-btn"
          aria-label={`Ask ${NAME}${unseen ? " (something new)" : ""}`}
          onClick={() => setOpen(true)}
          style={{
            position: "fixed", zIndex: 165, left: anchor.left, bottom: anchor.bottom,
            display: "flex", alignItems: "center", gap: 8, minHeight: 44,
            padding: "4px 14px 4px 4px", borderRadius: 22, cursor: "pointer",
            background: A.panel, border: `1px solid ${A.edgeHot}`, color: A.text,
            fontFamily: MK.sans, fontSize: 14, fontWeight: 600, letterSpacing: ".04em",
            boxShadow: "0 4px 18px rgba(0,0,0,.45)",
          }}>
          <span style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "radial-gradient(circle, #2b2266, #120f2a)" }}>
            <AemiliaSprite size={34} art={art} />
          </span>
          Ask {NAME}
          {unseen > 0 && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: A.star }} />}
        </button>
      )}

      {/* ── THE SHEET ── */}
      {open && (
        <>
          <div onClick={() => setOpen(false)} aria-hidden="true"
            style={{ position: "fixed", inset: 0, zIndex: 258, background: "rgba(4,2,14,0.35)" }} />
          <div role="dialog" aria-modal="true" aria-label={`Ask ${NAME}`}
            // keys stay in the sheet (the title's WASD never sees them) — so Escape
            // is handled HERE; the window listener only covers focus on the scrim.
            ref={sheetRef}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Tab" && sheetRef.current) {          // keep Tab inside the sheet
                const f = [...sheetRef.current.querySelectorAll("button, input")];
                if (f.length) {
                  const first = f[0], last = f[f.length - 1];
                  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
                }
              }
              e.stopPropagation();
            }}
            onKeyUp={(e) => e.stopPropagation()}
            style={{
              position: "fixed", zIndex: 260,
              ...(narrow
                ? { left: 0, right: 0, bottom: 0, maxHeight: "80dvh", borderRadius: "18px 18px 0 0" }
                // she lives in the band (right side): her sheet opens beside her house
                : inBand
                  ? { right: "calc(min(280px, 24vw) + 40px)", bottom: 16, width: "min(410px, calc(100vw - 28px))", maxHeight: "min(640px, calc(100vh - 80px))", borderRadius: 18 }
                  : { left: anchor.left, bottom: Math.max(16, anchor.bottom - 60), width: "min(410px, calc(100vw - 28px))", maxHeight: "min(640px, 80vh)", borderRadius: 18 }),
              display: "flex", flexDirection: "column", overflow: "hidden",
              background: A.panel, border: `1px solid ${A.edge}`, color: A.text,
              boxShadow: "0 18px 50px rgba(0,0,0,.6)", fontFamily: MK.sans,
              animation: "aemIn .22s ease-out",
            }}>
            {/* her, and what she's saying */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", padding: "16px 16px 10px" }}>
              <div style={{ flex: "0 0 auto" }}><AemiliaSprite size={58} art={art} /></div>
              <div aria-live="polite" style={{
                flex: 1, minWidth: 0, background: A.bubble, color: A.ink,
                borderRadius: "16px 16px 16px 4px", padding: "11px 14px",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                <div style={{ fontSize: 11, letterSpacing: ".18em", fontWeight: 700, color: "#5b45b8" }}>{NAME.toUpperCase()}</div>
                <div style={{ fontFamily: MK.display, fontSize: 16.5, lineHeight: 1.4 }}>{cur ? cur.a : GREETING}</div>
                {go && (
                  <button type="button" className="aem-btn"
                    onClick={() => { const run = go.run; skipReturn.current = true; setOpen(false); run(); }}
                    style={{
                      alignSelf: "flex-start", minHeight: 40, padding: "8px 16px", borderRadius: 20,
                      border: "none", background: A.ink, color: A.star, cursor: "pointer",
                      fontFamily: MK.sans, fontSize: 14, fontWeight: 700, letterSpacing: ".05em",
                    }}>Take me there ›</button>
                )}
              </div>
              <button ref={closeRef} type="button" className="aem-btn" aria-label="Close" onClick={() => setOpen(false)}
                style={{
                  alignSelf: "flex-start", flex: "0 0 auto", width: 40, height: 40, borderRadius: 20,
                  border: `1px solid ${A.edge}`, background: "transparent", color: A.text, cursor: "pointer", fontSize: 18,
                }}>×</button>
            </div>

            {/* tabs */}
            <div role="group" aria-label="Topics" style={{ display: "flex", gap: 8, padding: "4px 16px 10px", flexWrap: "wrap" }}>
              {tabs.map(([id, label]) => (
                <button key={id} type="button" aria-pressed={tab === id} className="aem-btn"
                  onClick={() => { setTab(id); setPicked(null); }}
                  style={{
                    minHeight: 36, padding: "6px 14px", borderRadius: 18, cursor: "pointer",
                    fontFamily: MK.sans, fontSize: 14, fontWeight: 600, letterSpacing: ".04em",
                    background: tab === id ? A.star : "transparent", color: tab === id ? A.ink : A.text,
                    border: `1px solid ${tab === id ? A.star : A.edge}`,
                  }}>{label}</button>
              ))}
            </div>

            {/* questions */}
            <div style={{ overflowY: "auto", padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              {list.map((x) => (
                <button key={x.id} type="button" className="aem-q"
                  onClick={() => { setPicked(x.id); markSeen(x.id); }}
                  style={{
                    textAlign: "left", minHeight: 46, padding: "10px 14px", borderRadius: 12, cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                    background: picked === x.id ? A.panelHi : "rgba(255,255,255,0.03)",
                    border: `1px solid ${picked === x.id ? A.edgeHot : A.edge}`, color: A.text,
                    fontFamily: MK.sans, fontSize: 16, letterSpacing: ".02em",
                  }}>
                  <span>{x.q}</span>
                  {!seen.has(x.id)
                    ? <span style={{ fontSize: 11, fontWeight: 700, color: A.star, letterSpacing: ".12em" }}>NEW</span>
                    : <span aria-hidden="true" style={{ color: A.star }}>›</span>}
                </button>
              ))}
            </div>

            {/* how she shows up */}
            <div style={{ borderTop: `1px solid ${A.edge}`, padding: "10px 16px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, letterSpacing: ".14em", color: A.dim, marginRight: 4 }}>SHOW ME AS</span>
              {[["backdrop", "In the menu"], ["scene", "Out on the screen"], ["button", "Just a button"]].map(([m, label]) => (
                <button key={m} type="button" className="aem-btn" aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  style={{
                    minHeight: 34, padding: "5px 12px", borderRadius: 17, cursor: "pointer",
                    fontFamily: MK.sans, fontSize: 13, fontWeight: 600,
                    background: mode === m ? A.panelHi : "transparent", color: mode === m ? A.star : A.dim,
                    border: `1px solid ${mode === m ? A.edgeHot : A.edge}`,
                  }}>{label}</button>
              ))}
              {HAS_KEYBOARD && (
                <label style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 13, color: A.dim, cursor: "pointer" }}>
                  <input type="checkbox" checked={mode === "off"}
                    onChange={(e) => setMode(e.target.checked ? "off" : (["backdrop", "scene", "button"].includes(lsGet(LS_BACK)) ? lsGet(LS_BACK) : "backdrop"))}
                    style={{ width: 16, height: 16, accentColor: A.star }} />
                  Hide me completely
                  {mode === "off" && <span style={{ color: A.star }}>· press {BACK_KEY} to bring me back</span>}
                </label>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
